/* eslint-disable i18next/no-literal-string */
import * as ImagePicker from 'expo-image-picker'
import { useCallback } from 'react'

interface UseUploadStrategiesOptions {
  onFileSelect: (file: File | { uri: string; name?: string; type?: string }) => void
  onError: (error: string) => void
}

export function useUploadStrategies({ onFileSelect, onError }: UseUploadStrategiesOptions) {
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        onFileSelect(file)
      }
    },
    [onFileSelect]
  )

  const pickImage = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync()
      if (status !== 'granted') {
        onError('Permission denied')
        return
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: false,
        quality: 0.8,
        allowsMultipleSelection: false,
      })

      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0]

        onFileSelect({
          uri: asset.uri,
          name: asset.fileName || undefined,
          type: asset.mimeType || undefined,
        })
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to pick image')
    }
  }, [onFileSelect, onError])

  const takePhoto = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync()
      if (status !== 'granted') {
        onError('Permission denied')
        return
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        allowsEditing: false,
        quality: 0.8,
      })

      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0]
        onFileSelect({
          uri: asset.uri,
          name: asset.fileName || undefined,
          type: asset.mimeType || undefined,
        })
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Failed to take photo')
    }
  }, [onFileSelect, onError])

  return {
    handleFileChange,
    pickImage,
    takePhoto,
  }
}
